import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { type ICache } from '../Cache';
import { OverExposedType } from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import { AccountWithExtraInfo, type Apis, GroupRtbs, type MiddlewareConfig, OverExposedAccount, Rtbs } from '../types';
import { Group, GroupPortfolioOptions, GroupWithExtraInfo } from '../types/Group.type';
import { Portfolio, PortfolioWithExtraInfo } from '../types/Portfolio.type';
import { formatOverExposedAccounts, formatPortfolio } from '../utils/dataUtils';
import type { BranchApi } from './BranchApi';
import { UserApi } from './UserApi';

@injectable()
export class GroupApi extends CachedResponseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.BranchApi) private readonly branchApi: BranchApi,
        @inject(ApiInjectToken.UserApi) protected readonly userApi: UserApi,
    ) {
        super(apis, config, cache);
    }

    async getGroups(): Promise<GroupWithExtraInfo[]> {
        const data = await Promise.all([this.apis.orbis.advisory.groups.groupsList(), this.branchApi.getAccounts()]);
        const groups = data[0].data as Group[];
        const accounts = data[1];

        return groups.map((group) => {
            const groupAccounts = accounts.filter((account) => group.accountIds.includes(account.accountId));

            const rtbs = groupAccounts.reduce<Rtbs>(
                (result, account) => {
                    result.balance += account.balance;
                    result.cashOnAccount += account.cashOnAccount;
                    result.markToMarket += account.markToMarket;
                    result.sodCashBalance += account.sodCashBalance;
                    result.sodMarginBalance += account.sodMarginBalance;
                    result.sodShortBalance += account.sodShortBalance;
                    result.totalDividendAmount += account.totalDividendAmount;
                    result.totalDividendYield += account.totalDividendYield;
                    return result;
                },
                {
                    balance: 0,
                    cashOnAccount: 0,
                    markToMarket: 0,
                    sodCashBalance: 0,
                    sodMarginBalance: 0,
                    sodShortBalance: 0,
                    totalDividendAmount: 0,
                    totalDividendYield: 0,
                },
            );

            return {
                ...group,
                ...rtbs,
                accounts: groupAccounts,
            };
        });
    }

    async getGroup(groupId: number): Promise<GroupWithExtraInfo | null> {
        const data = await this.getGroups();
        const group = data.find((group) => group.id === groupId);

        if (!group) {
            return null;
        }

        return group;
    }

    async getAccounts(groupId: number): Promise<AccountWithExtraInfo[]> {
        const data = await Promise.all([
            this.userApi.getUserInfo(),
            this.apis.orbis.advisory.groups.groupRtbs(`${groupId}`),
        ]);

        const rtbs = data[1].data as GroupRtbs[];
        const accounts = data[0].accountList;

        return rtbs.map((accountRtbs) => {
            const accountInfo = accounts.find((item) => item.accountId === accountRtbs.accountId)!;
            return {
                ...accountInfo,
                ...accountRtbs,
            };
        });
    }

    async getPortfolio(groupId: number, options?: GroupPortfolioOptions): Promise<PortfolioWithExtraInfo> {
        const { combined, currency, symbol } = options || {};
        const data = await Promise.all([
            this.getAccounts(groupId),
            this.getGroup(groupId),
            this.apis.orbis.advisory.groups.groupPortfolios(String(groupId), {
                // @ts-expect-error: // FIXME: missing query param (currency). Waiting for backend update
                currency,
                loadQuotes: true,
                loadIndustries: true,
                loadCost: !combined,
                combined,
            }),
        ]);

        const accounts = data[0];
        const group = data[1];
        const portfolio = data[2].data as Portfolio;

        return formatPortfolio(portfolio, accounts, [], group?.balance || 0, { symbol });
    }

    async getOverExposedAccounts(
        groupId: number,
        threshold: number,
        type: OverExposedType,
    ): Promise<OverExposedAccount[]> {
        const { data } = await this.apis.orbis.advisory.analytics.overexposureAnalysis(
            `${type}`,
            'group',
            `${groupId}`,
            { threshold },
        );
        const accounts = await this.branchApi.getAccounts();

        return formatOverExposedAccounts(data, type, accounts);
    }
}
